import React, { useMemo } from 'react';
import { ToolbarProps } from 'react-big-calendar';

type ViewMode = 'month' | 'week' | 'day';

type CalendarToolbarProps<TEvent extends object, TResource extends object> =
  ToolbarProps<TEvent, TResource> & {
    viewMode: ViewMode;
    onViewModeChange: (view: ViewMode) => void;
  };

const CalendarToolbar = <TEvent extends object, TResource extends object>({
  date,
  label,
  onNavigate,
  viewMode,
  onViewModeChange
}: CalendarToolbarProps<TEvent, TResource>) => {
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) =>
        new Date(2020, index, 1).toLocaleString('en-US', { month: 'long' })
      ),
    []
  );

  const currentYear = date.getFullYear();
  const yearOptions = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2],
    [currentYear]
  );

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDate = new Date(date);
    nextDate.setMonth(Number(event.target.value));
    onNavigate('DATE', nextDate);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDate = new Date(date);
    nextDate.setFullYear(Number(event.target.value));
    onNavigate('DATE', nextDate);
  };

  return (
    <div className="calendar-toolbar">
      <div className="calendar-toolbar-controls">
        <button type="button" className="btn btn-secondary" onClick={() => onNavigate('TODAY')}>
          Today
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => onNavigate('PREV')}>
          Prev
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => onNavigate('NEXT')}>
          Next
        </button>
      </div>
      <div className="calendar-toolbar-views">
        <select
          value={viewMode}
          onChange={(event) => onViewModeChange(event.target.value as ViewMode)}
        >
          <option value="month">Month</option>
          <option value="week">Week</option>
          <option value="day">Day</option>
        </select>
      </div>
      <div className="calendar-toolbar-selects">
        <select value={date.getMonth()} onChange={handleMonthChange}>
          {months.map((month, index) => (
            <option key={month} value={index}>
              {month}
            </option>
          ))}
        </select>
        <select value={date.getFullYear()} onChange={handleYearChange}>
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default CalendarToolbar;
